import { Divider, Stack, Tooltip } from '@mui/material'
import { WarningIcon } from '@phosphor-icons/react'
import { tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { ReactElement } from 'react'

export type TenantDiscrepancyTooltipInfo = {
    category: string
    detailsValue: string
    summaryValue: string
    discrepancyValue: string
    unit: string
}

export type TenantDiscrepancyTooltipCopy = {
    title: string
    description: string
    details: string
    summary: string
    discrepancy: string
}

export const DEFAULT_TENANT_DISCREPANCY_TOOLTIP_COPY: TenantDiscrepancyTooltipCopy = {
    title: 'Abweichung der Summen',
    description: 'Die Summe der Mieterdetails stimmt nicht mit der Angabe unter Zusammenfassung überein. Bitte überprüfen.',
    details: 'Mieterdetails',
    summary: 'Zusammenfassung',
    discrepancy: 'Abweichung',
}

export type TenantDiscrepancyTooltipProps = {
    discrepancy?: TenantDiscrepancyTooltipInfo
    children: ReactElement
    copy?: TenantDiscrepancyTooltipCopy
}

export function TenantDiscrepancyTooltip({
    children,
    discrepancy,
    copy = DEFAULT_TENANT_DISCREPANCY_TOOLTIP_COPY,
}: TenantDiscrepancyTooltipProps) {
    const { category, detailsValue, summaryValue, discrepancyValue, unit } = discrepancy ?? {}
    return (
        <Tooltip
            placement="top"
            slotProps={{
                tooltip: {
                    sx: {
                        padding: 0,
                        bgcolor: tokens.ColorSurfaceDefault,
                        border: `1px solid ${tokens.ColorBorderDefault}`,
                        borderRadius: tokens.SizingCornerM,
                        ...tokens.Flyout,
                    },
                },
            }}
            title={
                !discrepancy ? (
                    ''
                ) : (
                    <Stack direction="column" padding={tokens.SizingSpacing200} gap={tokens.SizingSpacing200}>
                        <Stack direction="column" gap={tokens.SizingSpacing100}>
                            <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
                                <WarningIcon size={tokens.SizingIconS} weight="fill" color={tokens.ColorIconWarning} />
                                <Typography variant="headline4" color={tokens.ColorTextWarningSubtle}>
                                    {copy.title}
                                </Typography>
                            </Stack>

                            <Typography variant="textXS" color={tokens.ColorTextBold}>
                                {category}
                            </Typography>

                            <Typography variant="textXS" color={tokens.ColorTextDefault}>
                                {copy.description}
                            </Typography>
                        </Stack>

                        <Stack direction="column" gap={tokens.SizingSpacing075}>
                            <Stack direction="row" gap={tokens.SizingSpacing100} justifyContent="space-between">
                                <Typography variant="textS" color={tokens.ColorTextDefault}>
                                    {copy.details}
                                </Typography>
                                <Stack direction="row" gap={tokens.SizingSpacing075}>
                                    <Typography variant="textS" color={tokens.ColorTextBold}>
                                        {detailsValue}
                                    </Typography>
                                    <Typography variant="textS" color={tokens.ColorTextSubtle}>
                                        {unit}
                                    </Typography>
                                </Stack>
                            </Stack>

                            <Stack direction="row" gap={tokens.SizingSpacing100} justifyContent="space-between">
                                <Typography variant="textS" color={tokens.ColorTextDefault}>
                                    {copy.summary}
                                </Typography>
                                <Stack direction="row" gap={tokens.SizingSpacing075}>
                                    <Typography variant="textS" color={tokens.ColorTextBold}>
                                        {summaryValue}
                                    </Typography>
                                    <Typography variant="textS" color={tokens.ColorTextSubtle}>
                                        {unit}
                                    </Typography>
                                </Stack>
                            </Stack>

                            <Divider sx={{ borderColor: tokens.ColorBorderDefault }} />

                            <Stack direction="row" gap={tokens.SizingSpacing100} justifyContent="space-between">
                                <Typography variant="textS" color={tokens.ColorTextDefault}>
                                    {copy.discrepancy}
                                </Typography>
                                <Stack direction="row" gap={tokens.SizingSpacing075}>
                                    <Typography variant="textS" color={tokens.ColorTextBold}>
                                        {discrepancyValue}
                                    </Typography>
                                    <Typography variant="textS" color={tokens.ColorTextSubtle}>
                                        {unit}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                )
            }
        >
            {children}
        </Tooltip>
    )
}
